import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { IoCheckmarkCircleOutline, IoWarningOutline, IoMailOutline } from 'react-icons/io5';
import './Register.css';
import './EmailVerification.css';
import { API_URL } from '../utils/networkConfig';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'resending'
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const emailParam = searchParams.get('email');
    const hashParam = searchParams.get('hash');
    
    if (!emailParam || !hashParam) {
      setStatus('error');
      setMessage('Invalid verification link. Please check your email for the correct link.');
      return;
    }

    verifyEmail(emailParam, hashParam);
  }, [searchParams]);

  const verifyEmail = async (email, hash) => {
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-email?email=${encodeURIComponent(email)}&hash=${hash}`);
      const data = await res.json();

      if (res.ok) {
        setStatus('success');
        setMessage(data.message || 'Email verified successfully! You can now sign in.');
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setStatus('error');
        setMessage(data.error || 'Email verification failed.');
        setEmail(email); // Set email for resend functionality
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('error');
      setMessage('Network error. Please try again.');
      setEmail(searchParams.get('email') || ''); // Set email for resend functionality
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setMessage('Email address not available for resending.');
      return;
    }

    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setStatus('success');
        setMessage('Verification email sent! Please check your email.');
      } else {
        setStatus('error');
        setMessage(data.error || 'Failed to resend verification email.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <IoCheckmarkCircleOutline className="status-icon success" />;
      case 'error':
        return <IoWarningOutline className="status-icon error" />;
      case 'resending':
        return <IoMailOutline className="status-icon info" />;
      default:
        return <div className="spinner"></div>;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'resending':
        return 'info';
      default:
        return 'info';
    }
  };

  return (
    <div className="register-container">
      <div className="register-header">
        <h2>Email Verification</h2>
        <p>AI Game Show Account Verification</p>
      </div>
      
      <div className={`verification-status ${getStatusColor()}`}>
        {getStatusIcon()}
        <div className="status-message">
          <h3>
            {status === 'verifying' && 'Verifying your email...'}
            {status === 'success' && 'Verification Successful!'}
            {status === 'error' && 'Verification Failed'}
            {status === 'resending' && 'Sending verification email...'}
          </h3>
          <p>{message}</p>
        </div>
      </div>

      {status === 'success' && (
        <div className="success-actions">
          <p className="redirect-message">
            You will be redirected to the login page in a few seconds...
          </p>
          <Link to="/login" className="register-btn">
            Go to Login Now
          </Link>
        </div>
      )}

      {status === 'error' && (
        <div className="error-actions">
          <div className="resend-section">
            <h4>Need a new verification email?</h4>
            <form onSubmit={handleResendVerification} className="register-form">
              <div className="form-group">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={status === 'resending'}
                />
              </div>
              <button 
                type="submit" 
                disabled={status === 'resending'} 
                className="register-btn"
              >
                {status === 'resending' ? (
                  <>
                    <div className="spinner"></div>
                    Sending...
                  </>
                ) : (
                  'Resend Verification Email'
                )}
              </button>
            </form>
          </div>
          
          <div className="alternative-actions">
            <Link to="/register" className="login-link">
              Register Again
            </Link>
            <span className="separator">or</span>
            <Link to="/login" className="login-link">
              Back to Login
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailVerification; 